import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ShieldWarning, CheckCircle, Warning, Pulse } from "@phosphor-icons/react";
import AdminSectionShell from "@/components/admin/AdminSectionShell";

/**
 * AdminComplianceHeatmap — practice-level compliance overview for admins.
 *
 * Each row = one doctor with an active patient population. The bucket
 * cells use heat-coded backgrounds so admin can spot drift at a glance
 * without parsing numbers:
 *
 *   on-track          → emerald (good)
 *   at-risk           → amber   (watch)
 *   needs-attention   → rose    (intervene)
 *   pending           → slate   (haven't started)
 *
 * Heat intensity within each row scales to the doctor's OWN active
 * population, not the network max. A small practice with 2 needs-attention
 * cases (out of 4) should look as alarming as a big practice with 50
 * needs-attention cases (out of 100) — same proportional problem.
 *
 * Click-through goes to the doctor's profile so admin can drill in.
 */
const BUCKET_META = {
    on_track: {
        label: "On track",
        hint: "7d avg ≥ 16h and logged in last 2 days",
        bg: "bg-emerald-50",
        bar: "bg-emerald-500",
        text: "text-emerald-800",
    },
    at_risk: {
        label: "At risk",
        hint: "7d avg 12-16h OR last log 3-4 days ago",
        bg: "bg-amber-50",
        bar: "bg-amber-500",
        text: "text-amber-800",
    },
    needs_attention: {
        label: "Needs attention",
        hint: "7d avg < 12h OR last log 5+ days ago",
        bg: "bg-rose-50",
        bar: "bg-rose-500",
        text: "text-rose-800",
    },
};

function scoreTone(score) {
    if (score >= 75) return { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300" };
    if (score >= 50) return { bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-300" };
    return { bg: "bg-rose-100", text: "text-rose-800", border: "border-rose-300" };
}

export default function AdminComplianceHeatmap() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get("/admin/practice-compliance-heatmap")
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.detail || e.message || "Failed to load"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <AdminSectionShell
                eyebrow="Practice compliance heatmap"
                tone="teal"
                description="Per-doctor compliance signal across the network."
                testid="admin-compliance-heatmap-loading"
                bodyPad
            >
                <div className="text-muted-foreground text-sm">Loading network compliance…</div>
            </AdminSectionShell>
        );
    }

    if (error || !data) {
        return (
            <AdminSectionShell
                eyebrow="Practice compliance heatmap"
                tone="teal"
                description="Per-doctor compliance signal across the network."
                testid="admin-compliance-heatmap-error"
                bodyPad
            >
                <div className="text-muted-foreground text-sm">{error || "No data available."}</div>
            </AdminSectionShell>
        );
    }

    const items = data.items || [];
    const totals = data.totals || {};
    const networkScore = data.network_compliance_score || 0;
    const networkTone = scoreTone(networkScore);

    return (
        <AdminSectionShell
            eyebrow="Practice compliance heatmap"
            tone="teal"
            description="Per-doctor compliance signal across the network."
            right={
                <div className="flex items-center gap-3 text-xs">
                    <span className="text-muted-foreground">{data.doctor_count} {data.doctor_count === 1 ? "practice" : "practices"} with active patients</span>
                    <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border font-semibold tabular-nums ${networkTone.bg} ${networkTone.text} ${networkTone.border}`}
                        data-testid="heatmap-network-score"
                        title="Network on-track % across all in-treatment patients"
                    >
                        <Pulse size={12} weight="fill" />
                        Network {networkScore}%
                    </span>
                </div>
            }
            bodyPad
            testid="admin-compliance-heatmap"
        >
            {/* iter100.252 — Network totals neutralised to match Platform
                Pulse. Icons keep their semantic colour (emerald/amber/rose)
                so admins can still spot buckets at a glance. Tone reveals
                on hover. */}
            <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="rounded-xl bg-card border border-border hover:bg-emerald-50/60 hover:border-emerald-200 transition-colors px-3 py-2.5 group" data-testid="heatmap-total-on-track">
                    <div className="flex items-center gap-1.5 text-emerald-600 text-[11px] font-bold uppercase tracking-wide">
                        <CheckCircle size={13} weight="fill" /> <span className="text-muted-foreground group-hover:text-emerald-800 transition-colors">On track</span>
                    </div>
                    <div className="font-display text-2xl font-bold text-foreground mt-0.5 tabular-nums">{totals["on-track"] || 0}</div>
                </div>
                <div className="rounded-xl bg-card border border-border hover:bg-amber-50/60 hover:border-amber-200 transition-colors px-3 py-2.5 group" data-testid="heatmap-total-at-risk">
                    <div className="flex items-center gap-1.5 text-amber-600 text-[11px] font-bold uppercase tracking-wide">
                        <Warning size={13} weight="fill" /> <span className="text-muted-foreground group-hover:text-amber-800 transition-colors">At risk</span>
                    </div>
                    <div className="font-display text-2xl font-bold text-foreground mt-0.5 tabular-nums">{totals["at-risk"] || 0}</div>
                </div>
                <div className="rounded-xl bg-card border border-border hover:bg-rose-50/60 hover:border-rose-200 transition-colors px-3 py-2.5 group" data-testid="heatmap-total-needs-attention">
                    <div className="flex items-center gap-1.5 text-rose-600 text-[11px] font-bold uppercase tracking-wide">
                        <ShieldWarning size={13} weight="fill" /> <span className="text-muted-foreground group-hover:text-rose-800 transition-colors">Needs attention</span>
                    </div>
                    <div className="font-display text-2xl font-bold text-foreground mt-0.5 tabular-nums">{totals["needs-attention"] || 0}</div>
                </div>
            </div>

            {/* Per-doctor rows */}
            {items.length === 0 ? (
                <div className="text-muted-foreground text-sm italic py-6 text-center">
                    No active in-treatment patients across the network yet.
                </div>
            ) : (
                <div className="overflow-x-auto -mx-2 px-2">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                <th className="text-left py-2 px-2 font-bold">Practice</th>
                                <th className="text-right py-2 px-2 font-bold">Active</th>
                                <th className="text-left py-2 px-2 font-bold w-[55%]">Compliance distribution</th>
                                <th className="text-right py-2 px-2 font-bold">Score</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row) => {
                                const active = row.total_active || 1;
                                const pctOnTrack = (row.on_track / active) * 100;
                                const pctAtRisk = (row.at_risk / active) * 100;
                                const pctNA = (row.needs_attention / active) * 100;
                                const tone = scoreTone(row.compliance_score);
                                return (
                                    <tr
                                        key={row.doctor_id}
                                        className="border-t border-border/50 hover:bg-secondary/30 transition-colors"
                                        data-testid={`heatmap-row-${row.doctor_id}`}
                                    >
                                        <td className="py-3 px-2">
                                            <Link
                                                to={`/admin/doctors/${row.doctor_id}`}
                                                className="block hover:text-primary transition-colors"
                                            >
                                                <div className="font-semibold">{row.doctor_name}</div>
                                                <div className="text-[11px] text-muted-foreground truncate max-w-[260px]">
                                                    {row.clinic_name || "—"}{row.city ? ` · ${row.city}` : ""}
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="py-3 px-2 text-right tabular-nums font-semibold">{row.total_active}</td>
                                        <td className="py-3 px-2">
                                            <HeatBar
                                                pctOnTrack={pctOnTrack}
                                                pctAtRisk={pctAtRisk}
                                                pctNA={pctNA}
                                                counts={{
                                                    on_track: row.on_track,
                                                    at_risk: row.at_risk,
                                                    needs_attention: row.needs_attention,
                                                }}
                                            />
                                        </td>
                                        <td className="py-3 px-2 text-right">
                                            <span
                                                className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-bold tabular-nums ${tone.bg} ${tone.text} ${tone.border}`}
                                            >
                                                {row.compliance_score}%
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Legend */}
            <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-muted-foreground">
                {Object.entries(BUCKET_META).map(([key, meta]) => (
                    <div key={key} className="flex items-center gap-1.5" title={meta.hint}>
                        <span className={`w-3 h-3 rounded-sm ${meta.bar}`} aria-hidden="true" />
                        <span className="font-semibold text-foreground/70">{meta.label}</span>
                        <span className="text-muted-foreground/80 hidden sm:inline">— {meta.hint}</span>
                    </div>
                ))}
            </div>
        </AdminSectionShell>
    );
}


function HeatBar({ pctOnTrack, pctAtRisk, pctNA, counts }) {
    return (
        <div className="flex h-7 w-full overflow-hidden rounded-md border border-border bg-secondary/60">
            {pctOnTrack > 0 && (
                <div
                    className="bg-emerald-500 flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ width: `${pctOnTrack}%` }}
                    title={`On track: ${counts.on_track} patient${counts.on_track === 1 ? "" : "s"}`}
                >
                    {pctOnTrack >= 10 ? counts.on_track : ""}
                </div>
            )}
            {pctAtRisk > 0 && (
                <div
                    className="bg-amber-500 flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ width: `${pctAtRisk}%` }}
                    title={`At risk: ${counts.at_risk} patient${counts.at_risk === 1 ? "" : "s"}`}
                >
                    {pctAtRisk >= 10 ? counts.at_risk : ""}
                </div>
            )}
            {pctNA > 0 && (
                <div
                    className="bg-rose-500 flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ width: `${pctNA}%` }}
                    title={`Needs attention: ${counts.needs_attention} patient${counts.needs_attention === 1 ? "" : "s"}`}
                >
                    {pctNA >= 10 ? counts.needs_attention : ""}
                </div>
            )}
        </div>
    );
}
